import { useState, useEffect } from "react";
import { getEmailLogs } from "../../lib/api";
import { DEFAULT_LOCALE, EMPTY_EMAIL_LOGS, TOAST_EMAIL_SENT, TOAST_EMAIL_ERROR, TOAST_NO_PERMISSION, PLH_SEARCH_EMAIL, TIP_EMAIL_PREVIEW } from "../../lib/constants";
import { Mail, AlertCircle, CheckCircle2, Loader2, Clock, RefreshCw, Search, ChevronLeft, ChevronRight, X, Eye } from "lucide-react";
import { toast } from "sonner";
import { logger } from "../../lib/logger";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";

import { EMAIL_STATUS as STATUS, EMAIL_FILTER_OPTIONS, EMAIL_LOG_PAGE_SIZE as PAGE_SIZE } from "../../lib/tabs";

const ADMIN_TOKEN = process.env.REACT_APP_ADMIN_TOKEN || "";
const adminHeaders = { "Content-Type": "application/json", "X-Admin-Token": ADMIN_TOKEN };

export default function AdminEmailLogs({ readOnly = false }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [resending, setResending] = useState(null);
  const [confirmLog, setConfirmLog] = useState(null);
  const [detailLog, setDetailLog] = useState(null);
  const [page, setPage] = useState(1);

  useEffect(() => {
    async function loadLogs() {
      try {
        const data = await getEmailLogs();
        setLogs(data || []);
      } catch (err) {
        logger.error("Erro ao carregar logs:", err);
      } finally {
        setLoading(false);
      }
    }
    loadLogs();
  }, []);

  useEffect(() => { setPage(1); }, [filter, search]);

  async function handleResend(log) {
    if (readOnly) { toast.warning(TOAST_NO_PERMISSION); return; }
    setResending(log.uid);
    try {
      const res = await fetch("/api/email", {
        method: "POST",
        headers: adminHeaders,
        body: JSON.stringify({ to: log.to_email, subject: log.subject, html: log.html_body }),
      });
      if (!res.ok) throw new Error("Falha ao reenviar");
      toast.success(TOAST_EMAIL_SENT);
    } catch (err) {
      toast.error(TOAST_EMAIL_ERROR);
    } finally {
      setResending(null);
    }
  }

  const filtered = logs.filter(l => {
    if (filter !== "all" && l.status !== filter) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!l.to_email?.toLowerCase().includes(q) && !l.subject?.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paged = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  if (loading) return <div className="flex items-center justify-center py-20"><div className="w-6 h-6 border-2 border-champagne border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div data-id="admin-email-logs" className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-champagne/10 flex items-center justify-center text-champagne shrink-0">
            <Mail size={16} />
          </div>
          <div>
            <h3 className="text-base font-medium text-softblack">Logs de Email</h3>
            <p className="text-xs text-warmgray mt-0.5">{logs.length} registos · {logs.filter(l => l.status === "failed").length} falhas · {logs.filter(l => l.status === "queued").length} na fila</p>
          </div>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-warmgray/60" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder={PLH_SEARCH_EMAIL} className="w-full pl-9 pr-3 py-2 text-xs border border-fendi/30 rounded-lg bg-white focus:border-champagne focus:ring-1 focus:ring-champagne/20 outline-none transition-all" />
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {EMAIL_FILTER_OPTIONS.map(f => (
            <button key={f.key} onClick={() => setFilter(f.key)}
              className={`px-3 py-1.5 text-[9px] font-bold tracking-wider uppercase rounded-full border transition-all ${filter === f.key ? "bg-softblack text-white border-softblack" : "bg-white text-warmgray border-fendi/30 hover:border-fendi/60"}`}>
              {f.label} <span className="opacity-60">({f.key === "all" ? logs.length : logs.filter(l => l.status === f.key).length})</span>
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white border border-fendi/40 rounded-xl overflow-hidden">
        {paged.length === 0 ? (
          <div className="p-10 text-center">
            <div className="w-12 h-12 mx-auto rounded-full bg-fendi/10 flex items-center justify-center mb-4">
              <Mail size={20} className="text-fendi/40" />
            </div>
            <p className="text-sm text-warmgray">{EMPTY_EMAIL_LOGS}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm whitespace-nowrap">
              <thead>
                <tr className="border-b border-fendi/20 bg-fendi/5">
                  <th className="px-4 py-3 text-[10px] uppercase tracking-wider text-warmgray font-medium text-left">Status</th>
                  <th className="px-4 py-3 text-[10px] uppercase tracking-wider text-warmgray font-medium text-left">Destinatário</th>
                  <th className="px-4 py-3 text-[10px] uppercase tracking-wider text-warmgray font-medium text-left">Data</th>
                  <th className="px-4 py-3 text-[10px] uppercase tracking-wider text-warmgray font-medium text-right">Ação</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-fendi/10">
                {paged.map(log => {
                  const s = STATUS[log.status] || STATUS.blocked;
                  const Icon = s.icon;
                  return (
                    <tr key={log.uid} className="hover:bg-fendi/5 transition-colors">
                      <td className="px-4 py-4">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold ${s.bg} ${s.color} ${s.border} border`}>
                          <Icon size={11} /> {s.label}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <button onClick={() => setDetailLog(log)} className="font-mono text-xs text-champagne hover:text-softblack underline underline-offset-2 transition-colors text-left">
                          {log.to_email}
                        </button>
                      </td>
                      <td className="px-4 py-4 text-xs text-warmgray whitespace-nowrap">
                        {new Date(log.created_at).toLocaleString(DEFAULT_LOCALE)}
                      </td>
                      <td className="px-4 py-4 text-right">
                        {log.html_body && (
                          <button onClick={() => setConfirmLog(log)} disabled={resending === log.uid}
                            className="inline-flex items-center gap-1 text-[10px] text-warmgray hover:text-champagne transition-colors disabled:opacity-50">
                            <RefreshCw size={11} className={resending === log.uid ? "animate-spin" : ""} />
                            {resending === log.uid ? "Enviando..." : "Reenviar"}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {filtered.length > PAGE_SIZE && (
        <div className="bg-white border border-fendi/40 rounded-xl p-3 md:p-4 flex flex-col sm:flex-row items-center justify-between gap-3 md:gap-4">
          <p className="text-xs md:text-sm text-warmgray">{(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, filtered.length)} de {filtered.length}</p>
          <div className="flex items-center gap-1 md:gap-2">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={safePage === 1}
              className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center border border-fendi/30 rounded-lg hover:bg-fendi/20 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
              <ChevronLeft size={14} />
            </button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let n;
              if (totalPages <= 5) n = i + 1;
              else if (safePage <= 3) n = i + 1;
              else if (safePage >= totalPages - 2) n = totalPages - 4 + i;
              else n = safePage - 2 + i;
              return (
                <button key={n} onClick={() => setPage(n)}
                  className={`w-7 h-7 md:w-9 md:h-9 flex items-center justify-center rounded-lg text-[11px] md:text-sm transition-colors ${safePage === n ? "bg-softblack text-white" : "border border-fendi/30 hover:bg-fendi/20"}`}>{n}</button>
              );
            })}
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={safePage === totalPages}
              className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center border border-fendi/30 rounded-lg hover:bg-fendi/20 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}

      {detailLog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-lg mx-4 w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-champagne/10 flex items-center justify-center text-champagne"><Mail size={15} /></div>
                <p className="text-sm font-medium text-softblack">Detalhes do Email</p>
              </div>
              <button onClick={() => setDetailLog(null)} className="p-1 text-warmgray hover:text-softblack"><X size={16} /></button>
            </div>
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-3 gap-2 pb-3 border-b border-fendi/10">
                <span className="text-[10px] uppercase tracking-wider text-warmgray font-medium">Status</span>
                <span className="col-span-2">
                  {(() => { const s = STATUS[detailLog.status] || STATUS.blocked; const Icon = s.icon; return <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold ${s.bg} ${s.color} ${s.border} border`}><Icon size={11} /> {s.label}</span>; })()}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-2 pb-3 border-b border-fendi/10">
                <span className="text-[10px] uppercase tracking-wider text-warmgray font-medium">Para</span>
                <span className="col-span-2 text-softblack font-mono text-xs">{detailLog.to_email}</span>
              </div>
              <div className="grid grid-cols-3 gap-2 pb-3 border-b border-fendi/10">
                <span className="text-[10px] uppercase tracking-wider text-warmgray font-medium">Assunto</span>
                <span className="col-span-2 text-softblack">{detailLog.subject}</span>
              </div>
              <div className="grid grid-cols-3 gap-2 pb-3 border-b border-fendi/10">
                <span className="text-[10px] uppercase tracking-wider text-warmgray font-medium">Data</span>
                <span className="col-span-2 text-warmgray">{new Date(detailLog.created_at).toLocaleString(DEFAULT_LOCALE)}</span>
              </div>
              {detailLog.error_message && (
                <div className="grid grid-cols-3 gap-2 pb-3 border-b border-fendi/10">
                  <span className="text-[10px] uppercase tracking-wider text-warmgray font-medium">Erro</span>
                  <span className="col-span-2 text-red-500 text-xs break-words">{detailLog.error_message}</span>
                </div>
              )}
              {detailLog.html_body && (
                <div>
                  <span className="text-[10px] uppercase tracking-wider text-warmgray font-medium block mb-2">Conteúdo HTML</span>
                  <div className="bg-fendi/5 border border-fendi/20 rounded-lg p-4 max-h-60 overflow-y-auto">
                    <iframe srcDoc={detailLog.html_body} title={TIP_EMAIL_PREVIEW} className="w-full h-48 border-0" />
                  </div>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-fendi/10">
              <button onClick={() => setDetailLog(null)} className="px-4 py-2 text-xs font-medium text-warmgray border border-fendi/30 rounded-lg hover:bg-fendi/5">Fechar</button>
              {detailLog.html_body && (
                <button onClick={() => { setConfirmLog(detailLog); setDetailLog(null); }}
                  className="px-4 py-2 text-xs font-medium text-white bg-softblack rounded-lg hover:opacity-90">Reenviar</button>
              )}
            </div>
          </div>
        </div>
      )}

      <AlertDialog open={!!confirmLog} onOpenChange={() => setConfirmLog(null)}>
        <AlertDialogContent className="bg-white border border-fendi/40 rounded-xl max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-lg font-medium text-softblack">Reenviar Email?</AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-warmgray leading-relaxed">
              {confirmLog && <>O email será reenviado para <strong className="text-softblack">{confirmLog.to_email}</strong> com o assunto <strong className="text-softblack">{confirmLog.subject}</strong>.</>}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex gap-3 mt-6">
            <AlertDialogCancel className="btn-ghost !border-fendi/30 !py-2 !px-6 text-xs">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => { handleResend(confirmLog); setConfirmLog(null); }}
              className="bg-softblack text-white hover:opacity-90 !py-2 !px-6 text-xs rounded-lg">Confirmar Reenvio</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
